import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import {
  EventColorCategory,
  ICalColorSet,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import { ResponseStatusEnum } from "@clockwise/schema";
import { formatEventTitle } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/formatEventTitle";
import { getEventStartEndTimeText } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/getEventStartEndTimeText";
import { getDateRange } from "@clockwise/web-commons/src/util/date.util";
import { IEventWithAnnotatedEvents } from "@clockwise/web-commons/src/util/event-category-coloring";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DateTime, Interval } from "luxon";
import { MultiCalEventsQuery } from "../apollo/__generated__/MultiCalEvents.generated";
import { MultiCalEvent, PlannerEventCardsByDay } from "../types";
import { toEventPermissions } from "./parseAICalendarEventsDataGQL";

export const parseMultiCalEventsDataGQL = (
  calendarData: MultiCalEventsQuery,
  weeksToProcess: string[],
  eventColorGetter: (event: IEventWithAnnotatedEvents) => ICalColorSet | null,
  eventCategoryGetter: (event: IEventWithAnnotatedEvents) => EventColorCategory | undefined | null,
): [PlannerEventCardsByDay, MultiCalEvent[]] => {
  const newEvents: MultiCalEvent[] = [];
  const newCardsByDay: PlannerEventCardsByDay = {};
  if (calendarData && getValue(calendarData.org)) {
    const calendarEvents = getValue(
      getValue(calendarData?.org, "Org")?.eventParentBaseWrapperByWeeksObject,
    )?.list;

    // Iterate over all events, within each eventParents, within each eventParentsByDay, and push into eventsByDay map.
    calendarEvents?.forEach?.((week) => {
      week.eventParentsByDay.forEach((byDay) => {
        byDay.eventParents?.forEach((eventParent) => {
          eventParent.events.forEach((e) => {
            getDateRange(
              DateTime.fromISO(e.startTime.dateOrDateTime, {
                zone: getRenderTimeZone(),
              }).toISODate(),
              DateTime.fromISO(e.endTime.dateOrDateTime, { zone: getRenderTimeZone() })
                .minus({ seconds: 1 })
                .toISODate(),
            ).forEach((dateISO) => {
              // Only process events that occur on weeksToProcess. Only process these week as
              // all day events or events that span into prior or next week can be present here, but
              // other events in that week may not be present.
              // For example, the prefetch call that fetches the week after the one being currently viewed may have an
              // all day event that spans into the week being viewed, and if we just processed each day that that event spans, we would
              // call setEventsByDay down at the bottom of this function with only that all day event, and not the events current being displayed.
              // We can't just merge the two arrays, as its a valid use-case for an event to be deleted an not appear in a followup fetch of a week.
              if (
                !weeksToProcess
                  .flatMap((w) => getDateRange(w, DateTime.fromISO(w).plus({ days: 6 }).toISO()))
                  .includes(dateISO)
              ) {
                return;
              }
              // Check if event exists in map already, if so skip adding to startDate.
              if (
                newCardsByDay[dateISO]?.find(
                  (event) => event.externalEventId === e.eventKey.externalEventId,
                )
              ) {
                return; // All day events show up each day they span, so skip adding to startDate if already have.
              }

              newEvents.push(e);

              const eventCategory = eventCategoryGetter(e);
              const eventCategoryColor = eventColorGetter(e);

              const dateConfig = { zone: getRenderTimeZone() };
              const currentDate = DateTime.fromISO(dateISO, dateConfig);
              const startDateTime = DateTime.fromISO(e.startTime.dateOrDateTime, dateConfig);
              const endDateTime = DateTime.fromISO(e.endTime.dateOrDateTime, dateConfig);
              const subText = getEventStartEndTimeText(startDateTime, endDateTime);

              const dayInterval = Interval.fromDateTimes(
                DateTime.max(startDateTime, currentDate.startOf("day")),
                DateTime.min(endDateTime, currentDate.endOf("day")),
              );

              const eventInterval = Interval.fromDateTimes(startDateTime, endDateTime);

              newCardsByDay[dateISO] = [
                ...(newCardsByDay[dateISO] || []),
                {
                  active: false,
                  annotaion: undefined,
                  attendeeCount: e.attendees.length,
                  attendeesCalendarIds: e.attendees.map((a) => a.urnValue),
                  calendarIds: e.calendarIds,
                  deemphasis: false,
                  eventCategory: eventCategory ?? undefined,
                  eventCategoryColor: eventCategoryColor ?? undefined,
                  eventPermissions: toEventPermissions(e.eventPermissions),
                  externalEventId: e.eventKey.externalEventId,
                  flexStatus: null,
                  interval: e.isAllDay ? eventInterval : dayInterval, // All day multi-day and interday all day events need to manage interval differently.
                  isAllDay: !!e.isAllDay,
                  isCancelled: !!e.isCancelled,
                  isCollapseable:
                    e.isClockwiseEvent &&
                    e.title === "❇️ Focus Time (via Clockwise)" &&
                    eventParent.smartHoldMatch?.__typename === "FocusTimeHold" &&
                    eventParent.smartHoldMatch.state?.__typename !== "Paused" &&
                    eventParent.smartHoldMatch.state?.__typename !== "Protected",
                  key: `${e.eventKey.externalEventId}-${dateISO}`,
                  locked: e.locked,
                  responseState: ResponseStatusEnum.NotApplicable,
                  subText,
                  text: formatEventTitle(e.title, e.locked),
                  transparency: e.transparency,
                  videoLink: null,
                  keyAddendum: e.calendarIds[0] + "-multi",
                },
              ];
            });
          });
        });
      });
    });
  }

  return [newCardsByDay, newEvents];
};
